import { resetRecordFormData } from "components/utils/modal-helper"
import { GenerateSensorsDto } from "features/iot/sensors/models/sensor-models"
import { useState, useCallback, useEffect } from "react"

export const mapSensorToFormData = (generateSensorsDto?: GenerateSensorsDto): Record<string, string> => ({
    serialNumberPrefix: generateSensorsDto?.serialNumberPrefix || "",
    numberOfSensors: String(generateSensorsDto?.numberOfSensors || 0),
    pitch: String(generateSensorsDto?.pitch || 0),
    numberOfResistiveElements: String(generateSensorsDto?.numberOfResistiveElements || 0),
    hardwareRevision: generateSensorsDto?.hardwareRevision || "",
    firmwareRevision: generateSensorsDto?.firmwareRevision || "",
    offset: String(generateSensorsDto?.offset || 0),
})

// Validation function to validate form fields
const validateForm = (formData: Record<string, string>) => {
    const errors: Record<string, string> = {}

    if (!formData.serialNumberPrefix.trim()) {
        errors.serialNumberPrefix = "Serial number Prefix is required"
    }

    if (!formData.numberOfSensors.trim()) {
        errors.numberOfSensors = "numberOfSensors is required"
    }

    if (!formData.pitch.trim()) {
        errors.pitch = "Pitch is required"
    }

    if (!formData.numberOfResistiveElements.trim()) {
        errors.numberOfResistiveElements = "Number of resistive elements is required"
    } else if (Number(formData.numberOfResistiveElements) <= 0) {
        errors.numberOfResistiveElements = "Must be a positive number"
    }

    if (!formData.hardwareRevision.trim()) {
        errors.hardwareRevision = "Hardware revision is required"
    }

    if (!formData.firmwareRevision.trim()) {
        errors.firmwareRevision = "Firmware revision is required"
    }

    if (!formData.offset.trim()) {
        errors.offset = "Offset is required"
    }
    return errors
}

export const useGenerateSensorForm = () => {
    const [formData, setFormData] = useState<Record<string, string>>({
        serialNumberPrefix: "",
        numberOfSensors: String(0),
        pitch: String(0),
        numberOfResistiveElements: String(0),
        hardwareRevision: "",
        firmwareRevision: "",
        offset: String(0),
    })
    const [formErrors, setFormErrors] = useState<Record<string, string>>({})

    const resetForm = () => {
        setFormData({
            serialNumberPrefix: "",
            numberOfSensors: "",
            offset: "",
            pitch: "",
            numberOfResistiveElements: "",
            hardwareRevision: "",
            firmwareRevision: "",
        })
        setFormErrors({})
    }

    // Initialize form data based on sensor
    useEffect(() => {
        resetRecordFormData(setFormData)
    }, [])

    // Handle form field changes
    const handleChange = useCallback((fieldName: string, value: string) => {
        setFormData(prevData => ({
            ...prevData,
            [fieldName]: value,
        }))
    }, [])

    // Handle form field validation
    const handleValidation = (fieldName: string, validationError: string | any): void => {
        setFormErrors(prevErrors => ({
            ...prevErrors,
            [fieldName]: validationError,
        }))
    }

    // Validate the entire form before submission
    const validateFormFields = () => {
        const errors = validateForm(formData)
        setFormErrors(errors)
        const hasErrors = Object.values(errors).some(error => error !== "")
        return !hasErrors // Return true if no errors
    }

    return {
        setFormData,
        setFormErrors,
        formData,
        formErrors,
        handleChange,
        handleValidation,
        resetForm,
        validateFormFields,
    }
}
